import React from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../../../elements';
import type { Theme } from '@mui/material/styles';
import {
  V4_BUTTON_DEFAULT,
  V4_DIALOG_CONTENT,
  V4_ICON_BUTTON_SMALL,
  V4_SMALL_SCREEN,
  V4_STICKY_HEADER
} from '../../../../theme/v4Compat';
import Edit from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { FarmContactViewModel, UserViewModel } from '../../../../models/user';
import { notifications } from '../../../../utils';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from '../../../../constants/constants';
import { UserRoleChip } from '../../../../elements';
import { UserService } from '../../../../hooks';

interface IUsersStaffSelectProps {
  onModalClose: (selectedUsers: UserViewModel[]) => void;
  users: UserViewModel[];
}

const UsersStaffSelect: React.FC<IUsersStaffSelectProps> = (props) => {
  const { onModalClose, users } = props;

  const [selectedUsers, setSelectedUsers] = React.useState<UserViewModel[]>([]);
  const [selectedContacts] = React.useState<FarmContactViewModel[]>([]);
  const [opened, setOpened] = React.useState<boolean>(false);
  const [loadedUsers, setLoadedUsers] = React.useState<UserViewModel[]>([]);
  const [usersLoading, setUsersLoading] = React.useState<boolean>(false);

  const [usersTableData, setUsersTableData] = React.useState({
    page: 0,
    itemsPerPage: DEFAULT_ROWS_PER_PAGE,
    total: 0
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);
  const { t } = useTranslation();
  const styles = sxStyles(theme);
  const userService = new UserService();

  // UI variables
  const title = t('f-com-email');

  // Lambdas
  const closeModal = () => {
    setOpened(false);
    onModalClose && onModalClose(selectedUsers);
  };

  const getFarmUsers = (page?: number, rowsPerPage?: number) => {
    const curPage = page ? page : 0;
    const rows = rowsPerPage ? rowsPerPage : usersTableData.itemsPerPage;

    setUsersLoading(true);

    userService
      .getFarmUsers(curPage + 1, rows)
      .then((result) => {
        setLoadedUsers(result.data);
        setUsersTableData({
          ...usersTableData,
          total: result.total,
          page: result.page - 1,
          itemsPerPage: result.size
        });
        setUsersLoading(false);
      })
      .catch(() => {
        setUsersLoading(false);
        notifications.error(t('f-usr-load-error'));
      });
  };

  const arrayContainsUser = (array: UserViewModel[], contact: UserViewModel): boolean =>
    array.find((c) => c.email === contact.email) !== undefined;

  const addToArray = <T,>(array: T[], obj: T, setData: (newValue: T[]) => void) => setData([...array, obj]);

  const removeFromArray = <T,>(array: T[], obj: T, setData: (newValue: T[]) => void) => {
    const found = array.find((o) => JSON.stringify(o) === JSON.stringify(obj));
    if (found) {
      const index = array.indexOf(found);
      array.splice(index, 1);
      setData([...array]);
    }
  };

  const handleUsersRowsPerPage = (rowsPerPage: number) => {
    setUsersTableData({ ...usersTableData, itemsPerPage: rowsPerPage });
    getFarmUsers(0, rowsPerPage);
  };

  const handleUsersTablePageChange = (newPage: number) => {
    getFarmUsers(newPage);
  };

  // Behavior
  React.useEffect(() => {
    if (opened) {
      getFarmUsers();
    }
  }, [opened]);

  React.useEffect(() => {
    if (users) {
      setSelectedUsers(users);
    }
  }, [users, selectedContacts]);

  return (
    <>
      <Dialog
        id="addSupplyRuleDialog"
        open={opened}
        onClose={closeModal}
        fullWidth={true}
        fullScreen={fullScreen}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
      >
        {fullScreen ? (
          <AppBar sx={styles.appBar}>
            <Toolbar>
              <Typography variant="h6" component="div" sx={styles.title}>
                {title}
              </Typography>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle>{title}</DialogTitle>
        )}

        <DialogContent sx={V4_DIALOG_CONTENT}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant={'subtitle1'}>{t('f-usr-title')}:</Typography>
              <Box sx={{ mt: 1 }}>
                <TableContainer component={Paper} elevation={1}>
                  <Table stickyHeader sx={V4_STICKY_HEADER}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" align="center">
                          <Badge color="primary" badgeContent={selectedUsers?.length}>
                            <Icon>check</Icon>
                          </Badge>
                        </TableCell>
                        <TableCell>{t('f-usr-name')}</TableCell>
                        <TableCell>{t('f-com-email')}</TableCell>
                        <TableCell>{t('f-usr-role')}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {usersLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : (
                        loadedUsers.map((user) => (
                          <TableRow key={user.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={arrayContainsUser(selectedUsers, user)}
                                onChange={(_event, pv) => {
                                  pv
                                    ? addToArray(selectedUsers, user, setSelectedUsers)
                                    : removeFromArray(selectedUsers, user, setSelectedUsers);
                                }}
                              />
                            </TableCell>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{<UserRoleChip role={user.role} />}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                          count={usersTableData.total}
                          rowsPerPage={usersTableData.itemsPerPage}
                          page={usersTableData.page}
                          onPageChange={(_event, newPage) => handleUsersTablePageChange(newPage)}
                          onRowsPerPageChange={(event) => {
                            handleUsersRowsPerPage(+event.target.value);
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal} autoFocus color="inherit" sx={V4_BUTTON_DEFAULT}>
            {t('m-task-status-done')}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', mt: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography>Emails:</Typography>

          <IconButton size="small" onClick={() => setOpened(true)} sx={V4_ICON_BUTTON_SMALL}>
            <Edit></Edit>
          </IconButton>
        </Box>

        {selectedContacts.length > 0 || selectedUsers.length > 0 ? (
          <Box>
            {selectedContacts.map((u) => (
              <Chip key={u.email} size="small" label={u.email} color="secondary" style={{ margin: 2 }} />
            ))}
            {selectedUsers.map((u) => (
              <Chip key={u.id} size="small" label={u.email} color="primary" style={{ margin: 2 }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex' }}>
            <Chip size="small" label={t('f-none')} color="default" style={{ margin: 2 }} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default UsersStaffSelect;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    appBar: {
      position: 'relative'
    },
    title: {
      marginLeft: theme.spacing(1),
      flex: 1
    }
  }) as const;
